import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Toolbar,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';

import { Save as SaveIcon, Cancel as CancelIcon } from '@material-ui/icons';

const ACTIONS = {
  EDIT: 'Edit',
  CREATE: 'Create',
  UPDATE: 'Update',
};

export function CreateUpdateEntityPortlet(props) {
  const {
    entity,
    initialData,
    EditorComponent,
    entityType,
    referenceData,
    saveButtonText,
    onSave,
    getEntityLabel,
    defaultText,
    additionalActions,
    onCancel,
    position,
    colour,
    ...restProps
  } = props;
  const [localEntity, setLocalEntity] = useState({ ...initialData, ...entity });
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const saveButtonDisabled = useMemo(
    () => isSaving || !isValid || isPerformingAction,
    [isSaving, isValid, isPerformingAction]
  );
  const title = useMemo(
    () =>
      entity
        ? `${ACTIONS.EDIT}: ${getEntityLabel(entity)}`
        : `${ACTIONS.CREATE}: ${entityType}`,
    [entity, entityType, getEntityLabel]
  );
  const calculatedSaveButtonText = useMemo(
    () => saveButtonText || (entity ? ACTIONS.UPDATE : ACTIONS.CREATE),
    [saveButtonText, entity]
  );
  const handleValidityChange = useCallback(
    (validity) => setIsValid(validity),
    []
  );
  const handlePropertyChange = (changes) =>
    setLocalEntity((existing) => ({ ...existing, ...changes }));
  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(localEntity);
    } catch (error) {
      setIsSaving(false);
    }
  };
  const getHandler = (onClick) => async () => {
    try {
      setIsPerformingAction(true);
      await onClick(localEntity);
    } catch (error) {
      setIsPerformingAction(false);
    }
  };
  const disableAdditionalActions = useMemo(
    () =>
      additionalActions.map(({ disableAction }) => disableAction(localEntity)),
    [localEntity, additionalActions]
  );
  const displayAdditionalActions = useMemo(
    () =>
      additionalActions.map(({ displayAction }) => displayAction(localEntity)),
    [localEntity, additionalActions]
  );
  const calculatedSaveButtonTooltip = useMemo(
    () =>
      `${
        saveButtonDisabled ? 'Invalid' : calculatedSaveButtonText
      } ${entityType}`,
    [saveButtonDisabled, entityType, calculatedSaveButtonText]
  );
  const buttons = (
    <>
      <Tooltip title="Cancel">
        <span>
          <Button
            style={{ marginLeft: 8 }}
            data-testid={`cancel-${position}`}
            color="secondary"
            variant="contained"
            onClick={onCancel}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={calculatedSaveButtonTooltip}>
        <span>
          <Button
            style={{ marginLeft: 8 }}
            data-testid={`save-${position}`}
            disabled={saveButtonDisabled}
            color="primary"
            variant="contained"
            onClick={handleSave}
            startIcon={isSaving ? <CircularProgress /> : <SaveIcon />}
          >
            {calculatedSaveButtonText}
          </Button>
        </span>
      </Tooltip>

      {additionalActions.map(
        (
          {
            onClick,
            message = '',
            displayAction = true,
            disableAction = false,
            tooltip,
            disabledTooltip,
            ...restProps
          },
          i
        ) =>
          displayAdditionalActions[i] && (
            <Tooltip
              key={i}
              title={disableAdditionalActions[i] ? disabledTooltip : tooltip}
            >
              <span>
                <Button
                  data-testid={`${message}-${position}`}
                  style={{ marginLeft: 8 }}
                  disabled={saveButtonDisabled || disableAdditionalActions[i]}
                  onClick={getHandler(onClick)}
                  {...restProps}
                >
                  {message}
                </Button>
              </span>
            </Tooltip>
          )
      )}
    </>
  );

  return (
    <>
      <Card style={{ margin: 8, backgroundColor: colour }}>
        <Toolbar>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Typography variant="h6">{title}</Typography>
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {position === 'top' && buttons}
          </div>
        </Toolbar>
        <CardContent style={{ backgroundColor: 'white', marginLeft: 8 }}>
          <EditorComponent
            entity={localEntity}
            referenceData={referenceData}
            defaultText={defaultText}
            onPropertyChange={handlePropertyChange}
            onValidityChange={handleValidityChange}
            validity={saveButtonDisabled}
            {...restProps}
          />
        </CardContent>
        {position === 'bottom' && (
          <CardActions data-testid="submit">{buttons}</CardActions>
        )}
      </Card>
    </>
  );
}

CreateUpdateEntityPortlet.propTypes = {
  /** Existing entity to edit */
  // eslint-disable-next-line react/forbid-prop-types
  entity: PropTypes.object,
  /** String to display entity type in title */
  entityType: PropTypes.string,
  /** Component that lets the user edit the entity */
  EditorComponent: PropTypes.func.isRequired,
  /** Any default values for an empty entity */
  // eslint-disable-next-line react/forbid-prop-types
  initialData: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  referenceData: PropTypes.object,
  /** Any dtext you would like entered by default */
  defaultText: PropTypes.string,
  /** Function thats called when the user hits the save button */
  onSave: PropTypes.func.isRequired,
  /** Function thats called when the user hits the cancel button */
  onCancel: PropTypes.func.isRequired,
  /** The text that the save button displays */
  saveButtonText: PropTypes.string,
  /** A function that returns a label for the given entity (default function returns the title or name property) */
  getEntityLabel: PropTypes.func,
  /** Position string. "top", "bottom". Default: "top" */
  position: PropTypes.string,
  /** The colour that the toolbar will display as */
  colour: PropTypes.string,
  /** Any additional functions or components that you would like the view to have */
  additionalActions: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func.isRequired,
      disableAction: PropTypes.func.isRequired,
      displayAction: PropTypes.func.isRequired,
      message: PropTypes.string.isRequired,
      tooltip: PropTypes.string.isRequired,
      disabledTooltip: PropTypes.string.isRequired,
    })
  ),
};

CreateUpdateEntityPortlet.defaultProps = {
  entity: null,
  initialData: null,
  referenceData: null,
  saveButtonText: null,
  getEntityLabel: ({ title, name }) => title || name || '',
  defaultText: '',
  additionalActions: [],
  position: 'top',
  colour: '#c8c9c8',
};
